/**
 * V2 Earned Media tabs — surfaced above the legacy summary cards when the
 * orchestrator-produced summary is detected (i.e. `recommendations_categorized`
 * or `engine_influence` or `missing_authorities` is present).
 *
 * Renders 5 detail tabs (Médias acquis · Médias à conquérir · Influence
 * moteurs IA · Conversations & forums · Précision) over the same
 * `EarnedMediaAuditSummary` v2 fields. The legacy v1 layout below this
 * component continues to render — it reads the fields shared between v1
 * and v2 (overall score, Wikipedia, E-E-A-T, sources tables, etc.) — so
 * no information is lost on either shape.
 */

import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddTaskIcon from '@mui/icons-material/AddTask';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import type {
  EarnedMediaAuditSummary,
  Recommendation,
  MissingAuthority,
  EngineInfluenceDomain,
  EngineInfluenceTrend,
  ConversationMention,
} from '@traaker/shared';

// Match Dashboard COLORS — narrative violet, authority green, referral orange.
const AXIS_COLOR = {
  narrative: '#7C3AED',
  authority: '#0E9F6E',
  referral: '#EA580C',
};

const SEVERITY_COLOR: Record<string, string> = {
  high: '#EF4444',
  medium: '#F59E0B',
  low: '#6B7280',
};

const SEVERITY_LABEL: Record<string, string> = {
  high: 'Élevé',
  medium: 'Moyen',
  low: 'Faible',
};

// English keys (match DDB / shared types) → French UI labels.
const CLASSIFICATION_LABEL: Record<string, string> = {
  media: 'média',
  public_media: 'média public',
  review_platform: 'plateforme d’avis',
  job_board: 'site emploi',
  directory: 'annuaire',
  industry_report: 'analyste / rapport',
  institution: 'institution',
  association: 'association',
  educational: 'éducation',
  social: 'social',
  gov: 'gouv.',
  corporate: 'entreprise',
  brand_owned: 'votre site',
  other: 'autre',
};

function classifLabel(cls?: string): string {
  if (!cls) return 'autre';
  return CLASSIFICATION_LABEL[cls.toLowerCase()] || cls;
}

// What "Médias acquis" actually displays. Used by BOTH the tab counter and
// the body filter so they never drift out of sync (which would produce
// "Médias acquis (2)" + "Aucun média acquis" in the same view — a real bug
// we hit on legacy `other`-classified rows).
//
// HIDDEN here means "not earned-media coverage":
//   - brand_owned, corporate → not external coverage at all
//   - social                  → routed to Conversations & forums tab instead
//   - other                   → classification failure, not a real category
// All other classifications (media, public_media, review_platform, directory,
// industry_report, institution, association, educational, gov) ARE shown.
const HIDDEN_ACQUIRED_CLASSIFICATIONS = new Set([
  'social', 'other', 'corporate', 'brand_owned',
]);

function filterAcquired(sources: any[]): any[] {
  return sources.filter((s) => {
    if ((s.mediaType || 'earned') === 'social') return false;
    const cls = (s.classification || '').toLowerCase();
    if (cls && HIDDEN_ACQUIRED_CLASSIFICATIONS.has(cls)) return false;
    return true;
  });
}

/**
 * Lightweight registered-domain extraction for legacy DDB rows that don't
 * carry a `domain` field. Mirrors the conservative heuristic from the agent
 * code in [_common.registered_domain]: strips scheme + path + leading `www.`,
 * keeps the last 2 (or 3 for known multi-part TLDs) segments. Not a full
 * eTLD parser — adequate for the citation URLs we see in earned-media rows.
 */
function extractRegDomain(url?: string | null): string {
  if (!url || typeof url !== 'string') return '';
  try {
    const raw = url.trim().toLowerCase();
    let host: string;
    if (!raw.includes('://') && !raw.includes('/') && raw.includes('.')) {
      host = raw;
    } else {
      const parsed = new URL(raw.includes('://') ? raw : `https://${raw}`);
      host = parsed.hostname || '';
    }
    if (host.startsWith('www.')) host = host.slice(4);
    const parts = host.split('.');
    if (parts.length < 2) return host;
    const lastTwo = parts.slice(-2).join('.');
    const TWO_PART_TLDS = new Set([
      'co.uk', 'co.jp', 'co.kr', 'co.in', 'co.za', 'co.nz', 'co.id',
      'com.au', 'com.br', 'com.cn', 'com.mx', 'com.tr', 'com.tw',
      'ac.uk', 'gov.uk', 'gov.fr', 'gouv.fr',
    ]);
    if (TWO_PART_TLDS.has(lastTwo) && parts.length >= 3) {
      return parts.slice(-3).join('.');
    }
    return lastTwo;
  } catch {
    return '';
  }
}

// ── Public component ──

export function EarnedMediaV2Tabs({
  summary,
  sources,
  inaccuracies,
  onCreateTask,
}: {
  summary: EarnedMediaAuditSummary;
  sources: any[];
  inaccuracies: any[];
  onCreateTask?: (title: string, severity?: 'high' | 'medium' | 'low') => void;
}) {
  const [tab, setTab] = useState(0);

  const counts = useMemo(() => {
    // Use the SAME filter the body applies, so the count and the rendered
    // table can't drift apart.
    const acquiredCount = filterAcquired(sources).length;
    // Missing tab also has a fallback path: if `missing_authorities` is
    // empty, the tab can fall back to legacy coverage_gaps, so the count
    // should reflect what the body actually renders.
    const missingFromV2 = summary.missing_authorities?.length || 0;
    const missingFallback =
      summary.coverage_gap_details?.length || summary.coverage_gaps?.length || 0;
    const missingCount = missingFromV2 || missingFallback;
    const influenceCount = summary.engine_influence?.topDomains?.length || 0;
    const conversationsCount = summary.conversations?.length || 0;
    const inaccCount = inaccuracies.length;
    return { acquiredCount, missingCount, influenceCount, conversationsCount, inaccCount };
  }, [sources, summary, inaccuracies]);

  return (
    <Card sx={{ mb: 3 }}>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
      >
        <Tab label={`Médias acquis (${counts.acquiredCount})`} />
        <Tab label={`Médias à conquérir (${counts.missingCount})`} />
        <Tab label={`Influence moteurs IA (${counts.influenceCount})`} />
        <Tab label={`Conversations & forums (${counts.conversationsCount})`} />
        <Tab label={`Précision (${counts.inaccCount})`} />
      </Tabs>
      <CardContent sx={{ p: 3 }}>
        {tab === 0 && (
          <AcquiredTab
            sources={sources}
            inaccuracies={inaccuracies}
            recommendations={
              summary.recommendations_categorized?.acquired_media || []
            }
            wikipediaScore={summary.wikipedia_quality_score}
            wikipediaPresence={summary.wikipedia_presence}
            wikipediaEnvergure={summary.wikipedia_envergure_context}
            onCreateTask={onCreateTask}
          />
        )}
        {tab === 1 && (
          <MissingTab
            missing={summary.missing_authorities || []}
            coverageGapDetails={summary.coverage_gap_details || []}
            coverageGaps={summary.coverage_gaps || []}
            recommendations={
              summary.recommendations_categorized?.missing_media || []
            }
            onCreateTask={onCreateTask}
          />
        )}
        {tab === 2 && (
          <InfluenceTab
            block={summary.engine_influence}
            sources={sources}
          />
        )}
        {tab === 3 && (
          <ConversationsTab conversations={summary.conversations || []} />
        )}
        {tab === 4 && (
          <PrecisionTab
            inaccuracies={inaccuracies}
            recommendations={
              summary.recommendations_categorized?.precision || []
            }
            onCreateTask={onCreateTask}
          />
        )}
      </CardContent>
    </Card>
  );
}

// ── Acquired tab ──

function AcquiredTab({
  sources,
  inaccuracies,
  recommendations,
  wikipediaScore,
  wikipediaPresence,
  wikipediaEnvergure,
  onCreateTask,
}: {
  sources: any[];
  inaccuracies: any[];
  recommendations: Recommendation[];
  wikipediaScore?: number;
  wikipediaPresence?: string;
  wikipediaEnvergure?: string;
  onCreateTask?: (t: string, s?: 'high' | 'medium' | 'low') => void;
}) {
  // Acquired = editorial press / blogs / directories / review platforms.
  // Defined once at module-scope (filterAcquired) to keep the tab counter
  // and the body in sync. Hides:
  //  - social (forums, Reddit threads — they belong in Conversations tab)
  //  - `other` / `gov` / `brand_owned` (not editorial coverage —
  //    excluded by the orchestrator, but we keep the client-side guard
  //    for back-compat with pre-2026-05-22 audits that were less strict)
  const acquired = filterAcquired(sources);

  // Index inaccuracies by registered domain so we can surface "X has Y issues"
  // directly inside the domain row instead of leaving a count badge.
  // Back-compat: pre-2026-05-22 inaccuracy rows lack the `domain` field
  // (only `sourceUrl` / `sourceName`). We derive a registered domain from
  // the URL or fall back to sourceName so old audits still display the
  // expand caret + inline issue list.
  const inaccByDomain = useMemo(() => {
    const m = new Map<string, any[]>();
    for (const i of inaccuracies || []) {
      const key = ((i.domain as string | undefined) || extractRegDomain(i.sourceUrl) || (i.sourceName || '')).toLowerCase();
      if (!key) continue;
      const list = m.get(key) || [];
      list.push(i);
      m.set(key, list);
    }
    return m;
  }, [inaccuracies]);

  // Empty state: no acquired sources yet. If the synthesizer produced
  // recommendations we surface them; otherwise we steer the user toward
  // the "Médias à conquérir" tab (which usually has actionable rows even
  // when the acquired list is empty).
  if (!acquired.length) {
    const hasRecs = recommendations.length > 0;
    return (
      <Box>
        <CalloutInfo>
          📚 <strong>Pourquoi cette liste compte</strong> — ces médias nourrissent
          l’autorité durable de votre marque. Pour ce cycle, aucun média acquis
          n’a été détecté.
        </CalloutInfo>
        <WikipediaScoreCard
          score={wikipediaScore}
          presence={wikipediaPresence}
          envergure={wikipediaEnvergure}
        />
        <Box sx={{ mt: 2 }}>
          <EmptyState
            text="Aucun média acquis détecté pour ce cycle."
            sub={
              hasRecs
                ? "Les recommandations ci-dessous indiquent les actions à initier."
                : "Consultez l'onglet « Médias à conquérir » pour voir les domaines à viser pour les prochains audits."
            }
          />
        </Box>
        {hasRecs && (
          <RecommendationList
            title="💡 Recommandations — médias acquis à amplifier"
            recs={recommendations}
            onCreateTask={onCreateTask}
          />
        )}
      </Box>
    );
  }

  return (
    <Box>
      <CalloutInfo>
        📚 <strong>Pourquoi cette liste compte</strong> — ces médias nourrissent
        l’autorité durable de votre marque. Ils alimentent les datasets
        d’entraînement des LLMs et continuent d’influencer les réponses de tous
        les moteurs IA — y compris ceux sans recherche web — pendant des mois
        après publication.
      </CalloutInfo>

      <WikipediaScoreCard
        score={wikipediaScore}
        presence={wikipediaPresence}
        envergure={wikipediaEnvergure}
      />

      <TableContainer sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Domaine</TableCell>
              <TableCell>Catégorie</TableCell>
              <TableCell>Sentiment</TableCell>
              <TableCell>Précision</TableCell>
              <TableCell>Récence</TableCell>
              <TableCell align="right">Score</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {acquired.map((s, i) => {
              // Same back-compat fallback for source rows: legacy rows may
              // miss `domain`. Try sourceUrl, then sourceName as last resort.
              const sourceKey = (
                (s.domain as string | undefined) ||
                extractRegDomain(s.sourceUrl) ||
                (s.sourceName || '')
              ).toLowerCase();
              return (
                <AcquiredRow
                  key={`${sourceKey || s.SK || i}-${i}`}
                  source={s}
                  inaccs={inaccByDomain.get(sourceKey) || []}
                  onCreateTask={onCreateTask}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {recommendations.length > 0 && (
        <RecommendationList
          title="💡 Recommandations — médias acquis à amplifier"
          recs={recommendations}
          onCreateTask={onCreateTask}
        />
      )}
    </Box>
  );
}

function AcquiredRow({
  source: s,
  inaccs,
  onCreateTask,
}: {
  source: any;
  inaccs: any[];
  onCreateTask?: (t: string, sev?: 'high' | 'medium' | 'low') => void;
}) {
  const [open, setOpen] = useState(false);
  const hasInaccs = inaccs.length > 0;
  const domain = s.domain || s.sourceName || '?';
  // Three precision states:
  //  - exact         : Haiku confirmed brandAccurate=true, no logged inaccuracies.
  //  - detailed      : N inaccuracies logged → expand row to see claim/réalité.
  //  - flagged-only  : Haiku set brandAccurate=false but didn't enumerate anything
  //                    (rare but happens). Chip shows "à vérifier" with no caret —
  //                    avoids promising details we don't have.
  const flaggedNoDetail = !hasInaccs && s.brandAccurate === false;
  const accurate = !hasInaccs && !flaggedNoDetail;

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: hasInaccs ? 'pointer' : 'default', '& > td': { borderBottom: hasInaccs && open ? 0 : undefined } }}
        onClick={() => hasInaccs && setOpen((v) => !v)}
      >
        <TableCell sx={{ fontWeight: 600 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            {hasInaccs && (
              <Box sx={{ fontSize: 11, color: '#EF4444' }}>{open ? '▾' : '▸'}</Box>
            )}
            <span>{domain}</span>
          </Stack>
        </TableCell>
        <TableCell>
          <Chip label={classifLabel(s.classification || s.sourceType)} size="small" variant="outlined" />
        </TableCell>
        <TableCell>
          <Chip
            label={s.sentiment || 'neutre'}
            size="small"
            sx={{
              bgcolor:
                s.sentiment === 'positive' ? '#ECFDF5' : s.sentiment === 'negative' ? '#FEF2F2' : '#EEF1F5',
            }}
          />
        </TableCell>
        <TableCell>
          {accurate && (
            <Chip label="exact" size="small" sx={{ bgcolor: '#ECFDF5', color: '#0E9F6E' }} />
          )}
          {hasInaccs && (
            <Chip
              label={`${inaccs.length} à corriger`}
              size="small"
              sx={{ bgcolor: '#FEF2F2', color: '#EF4444', fontWeight: 600 }}
            />
          )}
          {flaggedNoDetail && (
            <Tooltip title="Ce média a été marqué comme inexact mais le détail des erreurs n'a pas pu être extrait. Vérifiez la page manuellement.">
              <Chip
                label="à vérifier"
                size="small"
                sx={{ bgcolor: '#FFFBEB', color: '#B45309', fontWeight: 600 }}
              />
            </Tooltip>
          )}
        </TableCell>
        <TableCell>{s.recency || '—'}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 600 }}>
          {s.relevanceScore ?? 0}
        </TableCell>
        <TableCell align="right">
          {s.sourceUrl && (
            <IconButton
              size="small"
              component="a"
              href={s.sourceUrl}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          )}
        </TableCell>
      </TableRow>

      {hasInaccs && open && (
        <TableRow>
          <TableCell colSpan={7} sx={{ bgcolor: '#FFFAF6', p: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#991B1B', display: 'block', mb: 1 }}>
              ⚠ {inaccs.length} inexactitude{inaccs.length > 1 ? 's' : ''} détectée{inaccs.length > 1 ? 's' : ''}
              {' '}— voir l’onglet <strong>Précision</strong> pour les brouillons de correction.
            </Typography>
            <Stack spacing={1}>
              {inaccs.map((i, idx) => (
                <Box
                  key={`${i.SK || idx}`}
                  sx={{
                    p: 1.5,
                    bgcolor: 'white',
                    border: 1,
                    borderColor: i.severity === 'critical' ? '#FECACA' : '#FED7AA',
                    borderRadius: 1,
                    fontSize: 12.5,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Chip
                      label={i.severity === 'critical' ? 'critique' : 'mineure'}
                      size="small"
                      sx={{
                        bgcolor: i.severity === 'critical' ? '#FEF2F2' : '#FFFBEB',
                        color: i.severity === 'critical' ? '#EF4444' : '#F59E0B',
                        fontWeight: 600,
                        fontSize: 10,
                      }}
                    />
                    {i.sourceUrl && (
                      <Typography variant="caption" component="a" href={i.sourceUrl} target="_blank" rel="noreferrer" sx={{ color: '#2563EB', textDecoration: 'none' }}>
                        Article ↗
                      </Typography>
                    )}
                  </Stack>
                  <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                    <Box sx={{ p: 0.75, px: 1, bgcolor: '#FEF2F2', color: '#991B1B', borderRadius: 0.5, flex: 1 }}>
                      « {i.claim} »
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Box sx={{ p: 0.75, px: 1, bgcolor: '#ECFDF5', color: '#065F46', borderRadius: 0.5, flex: 1 }}>
                      {i.reality}
                    </Box>
                  </Box>
                  {onCreateTask && (
                    <Button
                      size="small"
                      startIcon={<AddTaskIcon fontSize="small" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onCreateTask(
                          `Corriger inexactitude ${i.sourceName || i.domain} : « ${i.claim} »`,
                          i.severity === 'critical' ? 'high' : 'medium',
                        );
                      }}
                      sx={{ mt: 1 }}
                    >
                      Créer une tâche corrective
                    </Button>
                  )}
                </Box>
              ))}
            </Stack>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// ── Missing tab ──

function MissingTab({
  missing,
  coverageGapDetails,
  coverageGaps,
  recommendations,
  onCreateTask,
}: {
  missing: MissingAuthority[];
  coverageGapDetails: { domain: string; category?: string; rationale?: string }[];
  coverageGaps: string[];
  recommendations: Recommendation[];
  onCreateTask?: (t: string, s?: 'high' | 'medium' | 'low') => void;
}) {
  // KPI strip metrics
  const totalLeverage = missing.reduce((acc, m) => acc + (m.leverageScore || 0), 0);
  const avgLeverage = missing.length ? Math.round(totalLeverage / missing.length) : 0;
  const citedByEngines = missing.filter(
    (m) =>
      m.citedByEngines &&
      (m.citedByEngines.narrative + m.citedByEngines.authority + m.citedByEngines.referral) > 0,
  ).length;

  // Fallback: if synthesizer didn't produce missing_authorities but the legacy
  // coverage_gaps array is populated, surface those so the tab isn't empty.
  const fallbackEntries: MissingAuthority[] = useMemo(() => {
    if (missing.length > 0) return [];
    const fromDetails = (coverageGapDetails || []).map((g) => ({
      domain: g.domain,
      category: g.category || 'trade_press',
      rationale: g.rationale || '',
      leverageScore: 50,
      citedByEngines: null,
      competitorsCovered: [],
      suggestedAngle: '',
      effort: 'medium' as const,
    }));
    if (fromDetails.length > 0) return fromDetails;
    return (coverageGaps || []).map((d) => ({
      domain: d,
      category: 'trade_press',
      rationale: '',
      leverageScore: 50,
      citedByEngines: null,
      competitorsCovered: [],
      suggestedAngle: '',
      effort: 'medium' as const,
    }));
  }, [missing.length, coverageGapDetails, coverageGaps]);

  const rows = missing.length > 0 ? missing : fallbackEntries;

  if (!rows.length && !recommendations.length) {
    return <EmptyState text="Aucun média à conquérir n'a été identifié pour ce cycle." />;
  }

  return (
    <Box>
      <CalloutInfo>
        🎯 <strong>Comment cette liste est construite</strong> — votre
        sous-catégorie est explorée empiriquement (recherche du leader puis
        extraction des médias qui le couvrent). Les domaines déjà cités par
        les moteurs IA dans vos audits Brand Perception sont remontés en
        priorité.
      </CalloutInfo>

      <Stack
        direction="row"
        spacing={2}
        sx={{
          mt: 2,
          mb: 1,
          flexWrap: 'wrap',
          '& > *': { minWidth: 140, flex: '1 1 0' },
        }}
      >
        <KpiTile label="Médias à conquérir" value={`${rows.length}`} />
        <KpiTile
          label="Levier IA moyen"
          value={`+${avgLeverage} pts`}
          accent={avgLeverage >= 60 ? '#0E9F6E' : avgLeverage >= 30 ? '#F59E0B' : '#6B7280'}
        />
        <KpiTile
          label="Cités par moteurs IA"
          value={`${citedByEngines} / ${rows.length}`}
          hint={citedByEngines > 0 ? 'contexte fort' : ''}
        />
        <KpiTile
          label="Quick wins (effort faible)"
          value={`${rows.filter((m) => m.effort === 'low').length}`}
        />
      </Stack>

      <TableContainer sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Domaine</TableCell>
              <TableCell>Catégorie</TableCell>
              <TableCell>Levier IA</TableCell>
              <Tooltip
                title="Concurrents co-cités avec ce domaine dans les réponses des moteurs IA (Perplexity, Gemini, ChatGPT search) sur 3+ occurrences. Signal indirect : les moteurs ont cité ce domaine dans des réponses qui mentionnaient aussi ces concurrents — pas une preuve causale que le domaine couvre éditorialement le concurrent."
                placement="top"
                arrow
              >
                <TableCell sx={{ cursor: 'help', textDecoration: 'underline dotted' }}>
                  Concurrents co-cités
                </TableCell>
              </Tooltip>
              <TableCell>Angle suggéré</TableCell>
              <TableCell>Effort</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((m) => (
              <TableRow key={m.domain}>
                <TableCell sx={{ fontWeight: 600 }}>{m.domain}</TableCell>
                <TableCell>
                  <Chip label={m.category} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  <Chip
                    label={`+${m.leverageScore} pts`}
                    size="small"
                    sx={{
                      bgcolor: m.leverageScore >= 70 ? '#ECFDF5' : m.leverageScore >= 40 ? '#FFFBEB' : '#FEF2F2',
                      color: m.leverageScore >= 70 ? '#0E9F6E' : m.leverageScore >= 40 ? '#F59E0B' : '#EF4444',
                      fontWeight: 600,
                    }}
                  />
                </TableCell>
                <TableCell>
                  {m.competitorsCovered?.length ? (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {m.competitorsCovered.slice(0, 3).map((c) => (
                        <Chip key={c} label={c} size="small" sx={{ fontSize: 11 }} />
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="caption" color="text.secondary">—</Typography>
                  )}
                </TableCell>
                <TableCell sx={{ maxWidth: 280 }}>
                  <Typography variant="caption">{m.suggestedAngle || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={m.effort === 'low' ? 'faible' : m.effort === 'high' ? 'élevé' : 'moyen'}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  {onCreateTask && m.suggestedAngle && (
                    <Tooltip title="Créer une tâche pour cet objectif">
                      <IconButton
                        size="small"
                        onClick={() =>
                          onCreateTask(`${m.domain} — ${m.suggestedAngle}`, 'medium')
                        }
                      >
                        <AddTaskIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {recommendations.length > 0 && (
        <RecommendationList
          title="💡 Recommandations — actions concrètes à créer"
          recs={recommendations}
          onCreateTask={onCreateTask}
        />
      )}
    </Box>
  );
}

// ── Influence tab ──

type InfluenceSortKey = 'total' | 'narrative' | 'authority' | 'referral' | 'domain';
type InfluenceFilter = 'all' | 'acquired' | 'missing';

function InfluenceTab({
  block,
  sources,
}: {
  block?: EarnedMediaAuditSummary['engine_influence'];
  sources: any[];
}) {
  const [sortKey, setSortKey] = useState<InfluenceSortKey>('total');
  const [sortDesc, setSortDesc] = useState(true);
  const [filter, setFilter] = useState<InfluenceFilter>('all');
  // null = all classifications visible. A Set means "show only these classifications".
  const [classificationFilter, setClassificationFilter] = useState<Set<string> | null>(null);
  const [showExcluded, setShowExcluded] = useState(false);

  if (!block || !block.topDomains?.length) {
    return (
      <EmptyState
        text="Lancez votre première Brand Perception pour enrichir cette analyse."
        sub="L’influence sur les moteurs IA est calculée à partir de vos audits Brand Perception (Narrative, Authority, Referral). Quand aucun cycle n’a tourné, ce panneau reste vide."
      />
    );
  }

  const acquiredDomains = new Set(sources.map((s) => s.domain).filter(Boolean));
  const trendsByDomain = new Map<string, EngineInfluenceTrend>();
  for (const t of block.domainTrends || []) trendsByDomain.set(t.domain, t);

  // Build classification counts so the chip strip shows live counts.
  const classificationCounts = new Map<string, number>();
  for (const d of block.topDomains) {
    const cls = (d.classification || 'other').toLowerCase();
    classificationCounts.set(cls, (classificationCounts.get(cls) || 0) + 1);
  }

  // Filter + sort
  const filtered = block.topDomains.filter((d) => {
    const acquired = d.isAcquired || acquiredDomains.has(d.domain);
    if (filter === 'acquired' && !acquired) return false;
    if (filter === 'missing' && acquired) return false;
    if (classificationFilter && classificationFilter.size > 0) {
      const cls = (d.classification || 'other').toLowerCase();
      if (!classificationFilter.has(cls)) return false;
    }
    return true;
  });
  const sorted = [...filtered].sort((a, b) => {
    let av: string | number;
    let bv: string | number;
    if (sortKey === 'domain') {
      av = a.domain;
      bv = b.domain;
    } else {
      av = a[sortKey] || 0;
      bv = b[sortKey] || 0;
    }
    if (av === bv) return 0;
    const cmp = av > bv ? 1 : -1;
    return sortDesc ? -cmp : cmp;
  });

  const setSort = (key: InfluenceSortKey) => {
    if (sortKey === key) {
      setSortDesc(!sortDesc);
    } else {
      setSortKey(key);
      setSortDesc(true);
    }
  };

  const SortHeader = ({ label, k, align = 'left' }: { label: string; k: InfluenceSortKey; align?: 'left' | 'right' }) => (
    <TableCell
      align={align}
      sx={{ cursor: 'pointer', userSelect: 'none', '&:hover': { color: 'primary.main' } }}
      onClick={() => setSort(k)}
    >
      {label}
      {sortKey === k && <Box component="span" sx={{ ml: 0.5, fontSize: 10 }}>{sortDesc ? '▾' : '▴'}</Box>}
    </TableCell>
  );

  const acquiredCount = block.topDomains.filter((d) => d.isAcquired || acquiredDomains.has(d.domain)).length;
  const missingCount = block.topDomains.length - acquiredCount;

  return (
    <Box>
      <CalloutInfo>
        🔭 <strong>Différent des médias acquis</strong> — ces domaines sont
        activement cités en temps réel par les moteurs IA dotés de recherche
        web (Perplexity, Gemini grounding, ChatGPT search) dans les réponses
        qu’ils donnent sur votre marque et votre catégorie. Levier court terme.
      </CalloutInfo>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2, mb: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: 4, color: 'text.secondary', fontSize: 12 }}>
          <span>
            <strong>{block.cyclesObserved}</strong> cycles agrégés
          </span>
          <span>
            Fenêtre : {block.windowStart || '—'} → {block.windowEnd || '—'}
          </span>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Stack direction="row" spacing={0.5}>
          <FilterChip label={`Tous (${block.topDomains.length})`} active={filter === 'all'} onClick={() => setFilter('all')} />
          <FilterChip label={`✓ Acquis (${acquiredCount})`} active={filter === 'acquired'} onClick={() => setFilter('acquired')} />
          <FilterChip label={`À conquérir (${missingCount})`} active={filter === 'missing'} onClick={() => setFilter('missing')} />
        </Stack>
      </Stack>

      {/* Classification multi-select. Shown only when ≥ 2 categories are
          present — single-category audits don't need the chip strip. */}
      {classificationCounts.size > 1 && (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{ mb: 2, flexWrap: 'wrap', rowGap: 0.5 }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1, fontWeight: 600 }}>
            Type :
          </Typography>
          <FilterChip
            label="Tous"
            active={classificationFilter === null || classificationFilter.size === 0}
            onClick={() => setClassificationFilter(null)}
          />
          {Array.from(classificationCounts.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([cls, count]) => {
              const active = !!classificationFilter?.has(cls);
              return (
                <FilterChip
                  key={cls}
                  label={`${classifLabel(cls)} (${count})`}
                  active={active}
                  onClick={() => {
                    setClassificationFilter((prev) => {
                      const next = new Set(prev || []);
                      if (next.has(cls)) {
                        next.delete(cls);
                      } else {
                        next.add(cls);
                      }
                      return next.size === 0 ? null : next;
                    });
                  }}
                />
              );
            })}
        </Stack>
      )}

      <TableContainer sx={{ mt: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortHeader label="Domaine" k="domain" />
              <TableCell>Catégorie</TableCell>
              <SortHeader label="Narrative" k="narrative" align="right" />
              <SortHeader label="Authority" k="authority" align="right" />
              <SortHeader label="Referral" k="referral" align="right" />
              <TableCell sx={{ minWidth: 110 }}>Distribution</TableCell>
              <SortHeader label="Total" k="total" align="right" />
              <TableCell sx={{ minWidth: 110 }}>Tendance</TableCell>
              <TableCell>Acquis ?</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((d) => (
              <TableRow key={d.domain}>
                <TableCell sx={{ fontWeight: 600 }}>{d.domain}</TableCell>
                <TableCell>
                  <Chip label={classifLabel(d.classification)} size="small" variant="outlined" />
                </TableCell>
                <TableCell align="right">{d.narrative}</TableCell>
                <TableCell align="right">{d.authority}</TableCell>
                <TableCell align="right">{d.referral}</TableCell>
                <TableCell>
                  <DistributionBar d={d} />
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {d.total}
                </TableCell>
                <TableCell>
                  <Sparkline trend={trendsByDomain.get(d.domain)} />
                </TableCell>
                <TableCell>
                  {d.isAcquired || acquiredDomains.has(d.domain) ? (
                    <Chip label="✓ acquis" size="small" sx={{ bgcolor: '#ECFDF5', color: '#0E9F6E' }} />
                  ) : (
                    <Chip label="à conquérir" size="small" sx={{ bgcolor: '#FFFBEB', color: '#F59E0B' }} />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {block.excludedDomains && block.excludedDomains.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Button
            size="small"
            onClick={() => setShowExcluded((v) => !v)}
            sx={{ color: 'text.secondary', fontSize: 12 }}
          >
            {showExcluded ? '▾' : '▸'} {block.excludedDomains.length} domaine{block.excludedDomains.length > 1 ? 's' : ''} filtré{block.excludedDomains.length > 1 ? 's' : ''} (vous, gov, ou non identifiés)
          </Button>
          {showExcluded && (
            <Box sx={{ mt: 1, p: 2, bgcolor: '#FAFBFC', borderRadius: 1, border: 1, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Si l’un de ces domaines vous semble être un média éditorial mal classé, signalez-le —
                la classification sera réévaluée au prochain cycle.
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {block.excludedDomains.map((e) => (
                  <Chip
                    key={e.domain}
                    label={`${e.domain} (${classifLabel(e.classification)}) · ${e.total} cit.`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: 11 }}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

function FilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <Chip
      label={label}
      size="small"
      onClick={onClick}
      sx={{
        bgcolor: active ? '#7C3AED' : '#EEF1F5',
        color: active ? 'white' : 'text.secondary',
        fontWeight: active ? 600 : 500,
        cursor: 'pointer',
        '&:hover': { bgcolor: active ? '#6D28D9' : '#E2E8F0' },
      }}
    />
  );
}

function DistributionBar({ d }: { d: EngineInfluenceDomain }) {
  const total = d.total || 1;
  const seg = (n: number, color: string) => (
    <Box sx={{ width: `${(n / total) * 100}%`, height: 8, bgcolor: color }} />
  );
  return (
    <Box sx={{ display: 'flex', height: 8, borderRadius: 1, overflow: 'hidden', bgcolor: '#F1F3F7' }}>
      {seg(d.narrative, AXIS_COLOR.narrative)}
      {seg(d.authority, AXIS_COLOR.authority)}
      {seg(d.referral, AXIS_COLOR.referral)}
    </Box>
  );
}

function Sparkline({ trend }: { trend?: EngineInfluenceTrend }) {
  if (!trend || trend.monthly.length < 2) {
    return <Typography variant="caption" color="text.secondary">—</Typography>;
  }
  const max = Math.max(...trend.monthly.map((m) => m.count), 1);
  const w = 100;
  const h = 22;
  const stepX = w / Math.max(trend.monthly.length - 1, 1);
  const points = trend.monthly
    .map((m, i) => `${i * stepX},${h - (m.count / max) * (h - 2) - 1}`)
    .join(' ');
  return (
    <svg width={w} height={h}>
      <polyline points={points} fill="none" stroke="#7C3AED" strokeWidth={1.5} />
    </svg>
  );
}

// ── Conversations tab ──

function ConversationsTab({ conversations }: { conversations: ConversationMention[] }) {
  if (!conversations.length) {
    return <EmptyState text="Aucune conversation publique détectée pour ce cycle." />;
  }
  return (
    <Box>
      <CalloutInfo>
        💬 Couvre les <strong>conversations publiques accessibles</strong> :
        Reddit + Hacker News + forums spécialisés. X (Twitter) et LinkedIn
        nécessitent des accès payants — un agent dédié les couvrira dans une
        version future.
      </CalloutInfo>

      <Stack spacing={2} sx={{ mt: 2 }}>
        {conversations.map((c, i) => (
          <Card key={`${c.platform}-${i}`} variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={c.platform}
                  size="small"
                  sx={{
                    bgcolor: c.platform === 'reddit' ? '#FEEFE6' : c.platform === 'hackernews' ? '#FFEAE0' : '#EEF1F5',
                    color: '#15171C',
                    fontWeight: 600,
                  }}
                />
                <Chip
                  label={
                    c.sentiment === 'positive' ? 'positif' : c.sentiment === 'negative' ? 'négatif' : 'neutre'
                  }
                  size="small"
                  variant="outlined"
                />
                {c.engagement?.comments != null && (
                  <Typography variant="caption" color="text.secondary">
                    {c.engagement.comments} commentaires
                  </Typography>
                )}
                {c.engagement?.upvotes != null && (
                  <Typography variant="caption" color="text.secondary">
                    · {c.engagement.upvotes} upvotes
                  </Typography>
                )}
              </Stack>
              {c.url && (
                <IconButton size="small" component="a" href={c.url} target="_blank" rel="noreferrer">
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {c.threadTitle || '(sans titre)'}
            </Typography>
            {c.excerpt && (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                « {c.excerpt} »
              </Typography>
            )}
            {c.suggestedAction && (
              <Box
                sx={{
                  mt: 1,
                  p: 1.5,
                  bgcolor: '#EFF6FF',
                  borderRadius: 1,
                  fontSize: 13,
                }}
              >
                💡 {c.suggestedAction}
              </Box>
            )}
          </Card>
        ))}
      </Stack>
    </Box>
  );
}

// ── Precision tab ──

function PrecisionTab({
  inaccuracies,
  recommendations,
  onCreateTask,
}: {
  inaccuracies: any[];
  recommendations: Recommendation[];
  onCreateTask?: (t: string, s?: 'high' | 'medium' | 'low') => void;
}) {
  if (!inaccuracies.length && !recommendations.length) {
    return <EmptyState text="Aucune inexactitude détectée — votre couverture tierce est factuellement alignée." />;
  }

  // Pair recommendations to inaccuracies by targetDomain when possible.
  const recsByDomain = new Map<string, Recommendation>();
  for (const r of recommendations) {
    if (r.targetDomain) recsByDomain.set(r.targetDomain, r);
  }

  return (
    <Box>
      <CalloutInfo>
        ⚠ Les moteurs IA s’appuient sur ces sources pour générer leurs
        réponses. Une inexactitude dans un média acquis se propage
        potentiellement dans plusieurs réponses IA par mois.
      </CalloutInfo>

      <Stack spacing={2} sx={{ mt: 2 }}>
        {inaccuracies.map((inacc, i) => {
          const matched = recsByDomain.get(inacc.domain || '');
          return (
            <PrecisionCard
              key={`${inacc.domain || 'i'}-${i}`}
              inacc={inacc}
              draftEmail={matched?.draftEmail}
              onCreateTask={onCreateTask}
            />
          );
        })}

        {/* Recommendations not paired to a specific inaccuracy */}
        {recommendations
          .filter((r) => !r.targetDomain || !inaccuracies.find((i) => i.domain === r.targetDomain))
          .map((r, i) => (
            <Card key={`rec-${i}`} variant="outlined" sx={{ p: 2 }}>
              <Typography variant="body2">{r.text}</Typography>
              {onCreateTask && (
                <Button
                  size="small"
                  startIcon={<AddTaskIcon />}
                  onClick={() => onCreateTask(r.text, r.severity)}
                  sx={{ mt: 1 }}
                >
                  Créer une tâche
                </Button>
              )}
            </Card>
          ))}
      </Stack>
    </Box>
  );
}

function PrecisionCard({
  inacc,
  draftEmail,
  onCreateTask,
}: {
  inacc: any;
  draftEmail?: string;
  onCreateTask?: (t: string, s?: 'high' | 'medium' | 'low') => void;
}) {
  const [showDraft, setShowDraft] = useState(false);
  const [draft, setDraft] = useState(draftEmail || '');

  const copyDraft = async () => {
    try {
      await navigator.clipboard.writeText(draft);
    } catch {
      /* no-op */
    }
  };

  const severity = inacc.severity === 'critical' ? 'high' : 'medium';
  return (
    <Card
      variant="outlined"
      sx={{
        p: 2,
        borderLeft: 4,
        borderLeftColor: severity === 'high' ? '#EF4444' : '#F59E0B',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          📰 {inacc.sourceName || inacc.domain}
        </Typography>
        <Chip
          label={inacc.severity === 'critical' ? 'critique' : 'mineure'}
          size="small"
          sx={{
            bgcolor: severity === 'high' ? '#FEF2F2' : '#FFFBEB',
            color: severity === 'high' ? '#EF4444' : '#F59E0B',
            fontWeight: 600,
          }}
        />
      </Stack>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 70, pt: 0.5 }}>
          Source dit
        </Typography>
        <Box
          sx={{
            flex: 1,
            p: 1,
            borderRadius: 1,
            bgcolor: '#FEF2F2',
            color: '#991B1B',
            fontSize: 13,
          }}
        >
          « {inacc.claim} »
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 70, pt: 0.5 }}>
          Réalité
        </Typography>
        <Box
          sx={{
            flex: 1,
            p: 1,
            borderRadius: 1,
            bgcolor: '#ECFDF5',
            color: '#065F46',
            fontSize: 13,
          }}
        >
          {inacc.reality}
        </Box>
      </Box>

      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        {draftEmail && (
          <Button size="small" onClick={() => setShowDraft((v) => !v)}>
            {showDraft ? 'Masquer le brouillon' : '📧 Voir brouillon de mail correctif'}
          </Button>
        )}
        {onCreateTask && (
          <Button
            size="small"
            startIcon={<AddTaskIcon />}
            onClick={() =>
              onCreateTask(
                `Corriger inexactitude ${inacc.sourceName || inacc.domain} : « ${inacc.claim} »`,
                severity,
              )
            }
          >
            Créer une tâche
          </Button>
        )}
      </Stack>

      {showDraft && draftEmail && (
        <Box sx={{ mt: 2, p: 2, bgcolor: '#FAFBFC', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Brouillon de mail à la rédaction (généré par Sonnet — vérifiez avant envoi)
          </Typography>
          <TextField
            multiline
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            fullWidth
            minRows={6}
            maxRows={14}
            sx={{ '& textarea': { fontFamily: 'monospace', fontSize: 12.5 } }}
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button
              size="small"
              startIcon={<ContentCopyIcon fontSize="small" />}
              onClick={copyDraft}
            >
              Copier
            </Button>
          </Stack>
        </Box>
      )}
    </Card>
  );
}

// ── Shared subcomponents ──

function KpiTile({
  label,
  value,
  hint,
  accent,
}: {
  label: string;
  value: string;
  hint?: string;
  accent?: string;
}) {
  return (
    <Card variant="outlined" sx={{ p: 1.5 }}>
      <Typography
        variant="caption"
        sx={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 0.4 }}
      >
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.25, color: accent || 'text.primary' }}>
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
          {hint}
        </Typography>
      )}
    </Card>
  );
}

/**
 * Wikipedia score explanation card.
 *
 * The Wikipedia quality score is graded against `BrandStrategy.envergure`,
 * not on a uniform rubric. A national SMB without a Wikipedia page can score
 * 70/100 because the Wikipedia notoriety bar excludes most SMBs by design —
 * recommending "create an article" would be misleading and would get rejected.
 * Users routinely confuse the score, so we explain it inline.
 *
 * Returns null when no Wikipedia data is available (legacy v1 audits or
 * pre-orchestrator runs).
 */
function WikipediaScoreCard({
  score,
  presence,
  envergure,
}: {
  score?: number;
  presence?: string;
  envergure?: string;
}) {
  if (score === undefined || score === null) return null;

  const presenceLabel: Record<string, string> = {
    own_page: 'Page Wikipédia dédiée',
    mentioned_in_others: 'Mentionnée dans d’autres articles',
    wikidata_only: 'Présence Wikidata uniquement',
    absent: 'Absente de Wikipédia',
  };
  const envLabel: Record<string, string> = {
    local: 'locale',
    regional: 'régionale',
    national: 'nationale',
    international: 'internationale',
    'hyper-local': 'hyper-locale',
    'multi-local': 'multi-locale',
    'mono-country': 'nationale',
    'multi-country': 'multi-pays',
    global: 'globale',
  };

  const env = (envergure || '').toLowerCase();
  const isLocalOrRegional = env === 'local' || env === 'regional' || env === 'hyper-local' || env === 'multi-local';
  const explanationLines: string[] = [];

  if (presence === 'absent' && isLocalOrRegional) {
    explanationLines.push(
      "Aucune page Wikipédia détectée. Pour une marque d'envergure " +
      `${envLabel[env] || env}, c'est attendu — le seuil de notoriété Wikipédia ` +
      "exclut la majorité des PME par design. Le score reste élevé : " +
      "viser une page Wikipédia n'est PAS une recommandation utile à ce stade."
    );
    explanationLines.push(
      "Action priorisée à la place : enrichir Wikidata (entité structurée, " +
      "moins exigeant en notoriété) puis viser des mentions dans des pages " +
      "thématiques (« Liste des éditeurs SaaS français », comparatifs, etc.)."
    );
  } else if (presence === 'absent' && env === 'national') {
    explanationLines.push(
      "Aucune page Wikipédia détectée. Pour une envergure nationale, le score " +
      "n'est pas pénalisé fortement, mais une page dédiée serait un signal " +
      "d'autorité fort — tentable si la marque a une couverture presse récurrente."
    );
  } else if (presence === 'absent' && env === 'international') {
    explanationLines.push(
      "Aucune page Wikipédia détectée alors que l'envergure est internationale. " +
      "C'est un manque significatif : la création d'une page (avec sources " +
      "tierces solides) est recommandée."
    );
  } else if (presence === 'own_page') {
    explanationLines.push(
      "Page Wikipédia dédiée détectée. Le score reflète la qualité de la " +
      "page (références, fraîcheur des éditions, langues couvertes). " +
      "Recommandation : maintenir les références à jour, surveiller les " +
      "modifications, ajouter des langues si pertinent pour la cible géographique."
    );
  } else if (presence === 'mentioned_in_others') {
    explanationLines.push(
      "La marque est mentionnée dans d'autres articles Wikipédia mais n'a " +
      "pas de page dédiée. Bon signal pour une couverture indirecte. " +
      "Selon l'envergure, viser une page dédiée peut être prématuré."
    );
  } else if (presence === 'wikidata_only') {
    explanationLines.push(
      "Présence Wikidata uniquement (entité structurée, sans page éditoriale). " +
      "C'est la première étape de la présence Wikipédia — utile pour " +
      "l'identification par les moteurs IA."
    );
  }

  if (!explanationLines.length) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        bgcolor: '#FFFBEB',
        border: 1,
        borderColor: '#FDE68A',
        borderRadius: 1.5,
        fontSize: 13,
        color: '#78350F',
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
        📖 Score Wikipédia : {score}/100 — {presenceLabel[presence || ''] || presence}
        {envergure && (
          <span style={{ fontWeight: 400, color: '#92400E' }}>
            {' · '}envergure {envLabel[env] || env}
          </span>
        )}
      </Typography>
      {explanationLines.map((line, i) => (
        <Typography key={i} variant="body2" sx={{ mt: i === 0 ? 0.5 : 0.75, lineHeight: 1.5 }}>
          {line}
        </Typography>
      ))}
    </Box>
  );
}

function CalloutInfo({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        p: 2,
        bgcolor: '#EFF6FF',
        border: 1,
        borderColor: '#93C5FD',
        borderRadius: 1.5,
        fontSize: 13,
        color: '#1E3A8A',
      }}
    >
      {children}
    </Box>
  );
}

function EmptyState({ text, sub }: { text: string; sub?: string }) {
  return (
    <Box sx={{ p: 5, textAlign: 'center' }}>
      <LightbulbIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
        {text}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, maxWidth: 480, mx: 'auto' }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
}

function RecommendationList({
  title,
  recs,
  onCreateTask,
}: {
  title: string;
  recs: Recommendation[];
  onCreateTask?: (t: string, s?: 'high' | 'medium' | 'low') => void;
}) {
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
        {title}
      </Typography>
      <Stack spacing={1}>
        {recs.map((r, i) => (
          <Card key={i} variant="outlined" sx={{ p: 1.5 }}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Chip
                label={SEVERITY_LABEL[r.severity] || r.severity}
                size="small"
                sx={{
                  bgcolor: `${SEVERITY_COLOR[r.severity] || '#6B7280'}20`,
                  color: SEVERITY_COLOR[r.severity] || '#6B7280',
                  fontWeight: 600,
                  fontSize: 11,
                  flexShrink: 0,
                }}
              />
              <Typography variant="body2" sx={{ flex: 1 }}>
                {r.text}
              </Typography>
              {onCreateTask && r.actionable && (
                <Tooltip title="Créer une tâche">
                  <IconButton size="small" onClick={() => onCreateTask(r.text, r.severity)}>
                    <AddTaskIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Card>
        ))}
      </Stack>
    </Box>
  );
}
